import { useState } from 'react';
import { apiGet, apiPost, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { useModal } from '../contexts/ModalContext';
import { PendingPlanChange } from '../components/PendingPlanChange';
import { LegalRevisionHistory } from '../components/LegalRevisionHistory';
import { LegalEditor } from '../components/LegalEditor';

/**
 * The live Terms of Service and Privacy Policy, and the campaign that tells
 * users when one of them changes (spec §6, L1).
 *
 * Everything on this screen is deliberately read-only except the two campaign
 * buttons. Authoring a revision is L2 and does not exist yet; showing the text
 * here with no way to edit it is honest about that, and it lets an admin
 * confirm what the website and app are actually serving right now.
 *
 * ── Why sending is a two-step ────────────────────────────────────────────────
 *
 * A legal notice reaches every account at once and cannot be recalled. So the
 * Send button stays disabled until the admin has sent themselves a test of
 * that exact revision, and the send itself is confirmed by typing the word,
 * not by clicking. The backend re-checks both — the disabled button is a
 * courtesy, not the control.
 */

type LegalDocumentType = 'terms' | 'privacy';

interface CampaignStatus {
  documentType: LegalDocumentType;
  revision: number | null;
  reason: string | null;
  publishedAt: string | null;
  audience: { total: number; withEmail: number };
  testSent: boolean;
}

interface CampaignResult {
  documentType: LegalDocumentType;
  revision: number;
  audience: number;
  sent: number;
  skipped: number;
  suppressed: number;
  notConfigured: number;
  failed: number;
  failures: { email: string; error: string }[];
}

const DOCUMENTS: {
  type: LegalDocumentType;
  label: string;
  publicPath: string;
}[] = [
  { type: 'terms', label: 'Terms of Service', publicPath: '/terms' },
  { type: 'privacy', label: 'Privacy Policy', publicPath: '/privacy' },
];

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—';

function DocumentCampaign({ type, label, publicPath }: (typeof DOCUMENTS)[number]) {
  const modal = useModal();
  const {
    data: status,
    loading,
    error,
    reload,
  } = useApiResource<CampaignStatus>(
    () => apiGet<CampaignStatus>(`/admin/legal/${type}/campaign`),
    [type],
  );

  const [busy, setBusy] = useState<'test' | 'send' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [result, setResult] = useState<CampaignResult | null>(null);

  const run = async (kind: 'test' | 'send', fn: () => Promise<void>) => {
    setBusy(kind);
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Request failed');
    } finally {
      setBusy(null);
    }
  };

  const sendTest = () =>
    run('test', async () => {
      const res = await apiPost<{ revision: number; sentTo: string }>(
        `/admin/legal/${type}/campaign/test`,
      );
      await modal.alert({
        title: 'Test sent',
        message: `Revision ${res.revision} of the ${label} notice was sent to ${res.sentTo}. Read it before sending to users — Send is now unlocked for this revision only.`,
      });
      reload();
    });

  const sendCampaign = async () => {
    if (!status?.revision) return;

    // Typing beats clicking for a send that cannot be recalled. The count is
    // spelled out because "up to 1,240 people" is the number that should give
    // an admin pause, not the revision number.
    const confirmed = await modal.promptConfirm({
      title: `Email all users about the ${label}?`,
      message: `This sends the revision ${status.revision} notice to up to ${status.audience.withEmail.toLocaleString()} of ${status.audience.total.toLocaleString()} accounts. Users who turned email notifications off are skipped. It cannot be undone or recalled.\n\nType SEND to confirm.`,
      matchText: 'SEND',
      confirmLabel: 'Send to all users',
      danger: true,
    });
    if (!confirmed) return;

    await run('send', async () => {
      const res = await apiPost<CampaignResult>(`/admin/legal/${type}/campaign/send`);
      setResult(res);
    });
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">{label}</h2>
      </div>
      <div className="card-body">
        {loading && !status && <p className="cell-sub">Loading…</p>}
        {error && <div className="error-banner">{error}</div>}

        {status && status.revision === null && (
          <p className="cell-sub">
            Nothing published yet. Run <code>npm run seed:legal</code> in the backend to import
            revision 1 from the website.
          </p>
        )}

        {status && status.revision !== null && (
          <>
            <div className="stat-card-grid">
              <div className="card stat-card">
                <div className="card-header">
                  <h3 className="card-title">Live revision</h3>
                </div>
                <div className="card-body">
                  <span className="stat-card-value">v{status.revision}</span>
                  <span className="stat-card-meta">Published {formatDate(status.publishedAt)}</span>
                </div>
              </div>
              <div className="card stat-card">
                <div className="card-header">
                  <h3 className="card-title">Accounts</h3>
                </div>
                <div className="card-body">
                  <span className="stat-card-value">{status.audience.total.toLocaleString()}</span>
                  <span className="stat-card-meta">Total users</span>
                </div>
              </div>
              <div className="card stat-card">
                <div className="card-header">
                  <h3 className="card-title">Reachable</h3>
                </div>
                <div className="card-body">
                  <span className="stat-card-value">
                    {status.audience.withEmail.toLocaleString()}
                  </span>
                  {/* Upper bound, not a promise: the opt-out filter runs per
                      recipient at send time, so this cannot be exact here. */}
                  <span className="stat-card-meta">Have an email address (before opt-outs)</span>
                </div>
              </div>
            </div>

            <p className="cell-sub">
              <strong>Reason for this revision:</strong> {status.reason || '—'}
            </p>
            <p className="cell-sub">
              Served live at <code>{publicPath}</code> on mediclynk.com and in the app.
            </p>

            {actionError && (
              <div role="alert" className="error-banner">
                {actionError}
              </div>
            )}

            {result && (
              <div className="card" style={{ marginTop: '1rem' }}>
                <div className="card-header">
                  <h3 className="card-title">Last send — revision {result.revision}</h3>
                </div>
                <div className="card-body">
                  {/* "Sent" means handed to the mail provider — opt-outs are
                      counted apart from it, so this line answers "who was
                      actually notified" rather than "who did we attempt". */}
                  <p className="cell-sub" data-testid="campaign-result">
                    {`${result.sent.toLocaleString()} sent · ${result.suppressed.toLocaleString()} opted out · ${result.skipped.toLocaleString()} no email address · ${result.failed.toLocaleString()} failed`}
                  </p>
                  {result.notConfigured > 0 && (
                    /* Not a user-preference outcome — the environment has no
                       mail provider key, so nothing was delivered at all. Said
                       plainly so this is not mistaken for a quiet audience. */
                    <div role="alert" className="error-banner" data-testid="campaign-unconfigured">
                      {`${result.notConfigured.toLocaleString()} notices were not sent because the mail provider is not configured in this environment. Nobody was notified.`}
                    </div>
                  )}
                  {(result.failures?.length ?? 0) > 0 && (
                    <div className="table-wrap">
                      <table className="data-table dt-table">
                        <thead>
                          <tr>
                            <th>Recipient</th>
                            <th>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.failures?.map((f) => (
                            <tr key={f.email}>
                              <td>{f.email}</td>
                              <td className="cell-sub">{f.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="plan-card-footer">
              {!status.testSent && (
                <span className="cell-sub">
                  Send yourself a test of v{status.revision} to unlock the campaign.
                </span>
              )}
              <button type="button" className="btn" onClick={sendTest} disabled={busy !== null}>
                {busy === 'test' ? 'Sending…' : 'Send test to me'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={sendCampaign}
                disabled={busy !== null || !status.testSent}
                title={
                  status.testSent
                    ? undefined
                    : 'Send yourself a test of this revision before emailing users'
                }
              >
                {busy === 'send' ? 'Sending…' : 'Email all users'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function LegalDocumentsPage() {
  // Saving a draft, discarding one, or restoring a revision all change the
  // history below. Remounting it is cheap and avoids each child growing its
  // own subscription to the others.
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Legal Documents</h1>
          <p>
            The Terms of Service and Privacy Policy the website and app are serving right now, and
            the campaign that tells users when one changes. A published revision is never edited — a
            correction is the next revision — so a user who accepted v11 can always be shown exactly
            what v11 said.
          </p>
        </div>
      </div>

      {/* The approval queue sits above everything: a parked change means the
          documents below are NOT what the next version will say. */}
      <PendingPlanChange />

      {DOCUMENTS.map((doc) => (
        <DocumentCampaign key={doc.type} {...doc} />
      ))}

      {DOCUMENTS.map((doc) => (
        <LegalEditor
          key={`editor-${doc.type}`}
          type={doc.type}
          label={doc.label}
          onChanged={() => setRefreshKey((k) => k + 1)}
        />
      ))}

      {DOCUMENTS.map((doc) => (
        <LegalRevisionHistory
          key={`history-${doc.type}-${refreshKey}`}
          type={doc.type}
          label={doc.label}
          onChanged={() => setRefreshKey((k) => k + 1)}
        />
      ))}
    </div>
  );
}
